import { makeAutoObservable, runInAction } from 'mobx';
import type { ApiClient, Application } from '@cef-ai/wallet-api-client';

export interface ApplicationsStoreOptions {
  apiClient: ApiClient;
  /**
   * Returns the current appId this store represents. Spec §4: comes from
   * EmbedWalletOptions, but the value lives on `appContextStore.app` and may
   * change after `wallet:hello`, so we read it through a closure.
   */
  getAppId: () => string;
  /** Returns the current user's Cere SS58 address. */
  getAddress: () => string;
}

export interface SaveCurrentAppInput {
  permissions: Record<string, unknown>;
  email?: string;
}

/**
 * Per-app permissions store. Spec §2.3, §4.4.
 *
 * Owns the lifecycle of the local cache of `currentApp` — the application
 * record this user has saved (if any) for the appId this store represents.
 * Underlying HTTP goes through `@cef-ai/wallet-api-client.applications.*`.
 */
export class ApplicationsStore {
  private _currentApp: Application | null = null;

  constructor(private readonly opts: ApplicationsStoreOptions) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get appId(): string {
    return this.opts.getAppId();
  }
  get currentApp(): Application | null {
    return this._currentApp;
  }

  /**
   * Load (or reload) the per-app record for this user + appId. Returns the
   * loaded application or a placeholder empty record if the API returns no
   * matches.
   */
  async loadCurrentApp(): Promise<Application> {
    const address = this.opts.getAddress();
    const appId = this.opts.getAppId();
    const records = await this.opts.apiClient.applications.find({ address, appId });
    const found = records.find((r) => r.appId === appId);
    const result: Application = found ?? {
      appId,
      address,
      permissions: {},
    };
    runInAction(() => {
      this._currentApp = result;
    });
    return result;
  }

  /** Save permissions for this app + user; updates the local cache. */
  async saveCurrentApp(input: SaveCurrentAppInput): Promise<Application> {
    const saved = await this.opts.apiClient.applications.save({
      appId: this.opts.getAppId(),
      permissions: input.permissions,
      email: input.email,
    });
    runInAction(() => {
      this._currentApp = saved;
    });
    return saved;
  }

  /**
   * List all applications this user has saved. Calls
   * `apiClient.applications.find({ address })` (with no `appId` filter).
   *
   * Note: this is NOT cached — each call hits the API. The wallet UI on
   * `/applications` calls it once on mount.
   */
  async listAll(): Promise<Application[]> {
    const address = this.opts.getAddress();
    return this.opts.apiClient.applications.find({ address });
  }

  /** Clear the cached app. Called on logout. */
  reset(): void {
    this._currentApp = null;
  }
}
